import React, {forwardRef, useCallback, useContext, useMemo, useState} from "react";
import DataGridBodyCell from "../cell";
import {DataGridColumnPinnedTypes, DataGridControllerContext, DataGridMiscContext, DEFAULT_DATA_GRID_MIN_ROW_HEIGHT,} from "../../../../index";
import classnames from "classnames";
import DataGridBodyRowDetailedPanel from "../detailed-panel";
import DataGridBodyGroupedRows from "../grouped-rows";

/**
 * @param {DataGridInternalRow} row
 * @param {DataGridDensities} density
 * @param {string} className
 * @param {Function} startScrolling
 * @param {number} leadingSpacerWidth
 * @param {number} trailingSpacerWidth
 * @param {number} estimateRowHeight
 * @param {number} minRowHeight
 * @param {string[]|undefined} visibleCenterColumnKeys
 * @param {boolean} columnsEnabled
 * @param {function(*): (number|undefined) | undefined} getLockedRowHeight
 * @param {React.Ref<HTMLTableRowElement>} ref
 */
const _DataGridBodyRow = ({
                              row,
                              density,
                              className,
                              startScrolling,
                              leadingSpacerWidth = 0,
                              trailingSpacerWidth = 0,
                              estimateRowHeight,
                              minRowHeight = DEFAULT_DATA_GRID_MIN_ROW_HEIGHT,
                              visibleCenterColumnKeys,
                              columnsEnabled = false,
                              getLockedRowHeight,
                          }, ref) => {
    const {classNames, singleSelectionOnly} = useContext(DataGridMiscContext);
    const dataGridApi = useContext(DataGridControllerContext);
    const [detailedPanelOpen, setDetailedPanelOpen] = useState(false);
    const [groupedRowsOpen, setGroupedRowsOpen] = useState(false);

    const hasGroupedRows = useMemo(() => row?.groupedRows?.length > 0, [row?.groupedRows])

    const groupedRowsVisible = useMemo(() => hasGroupedRows && groupedRowsOpen, [hasGroupedRows, groupedRowsOpen])

    const hasDetailedPanel = useMemo(() => (typeof row.detailedPanel !== 'undefined' && row.detailedPanel !== null), [row.detailedPanel])

    const detailedPanelVisible = useMemo(() => hasDetailedPanel && detailedPanelOpen, [hasDetailedPanel, detailedPanelOpen])

    const toggleSelection = useCallback(() => {
        if (singleSelectionOnly) {
            dataGridApi.resetSelection(true);
        }
        dataGridApi.toggleSelectedRows({
            [row.key]: !row.selected,
        }, true);
    }, [singleSelectionOnly, dataGridApi, row.key, row.selected]);

    const getColWidth = React.useCallback((col) => Math.max(col?.width?.size ?? 0, col?.width?.minWidth ?? 0), []);

    const contentMinHeight = Math.max(minRowHeight, row?.style?.minHeight != null ? Number(row.style.minHeight) : 0);
    const lockedRowHeight = columnsEnabled ? getLockedRowHeight?.(row?.key) : undefined;
    const trMinHeight = Math.max(contentMinHeight, Number(lockedRowHeight) || 0);

    const groupedRowVirtProps = columnsEnabled ? {
        leadingSpacerWidth,
        trailingSpacerWidth,
        estimateRowHeight,
        minRowHeight,
        visibleCenterColumnKeys,
        columnsEnabled,
        getLockedRowHeight,
    } : {};

    const cells = useMemo(() => {
        const source = row?.cells;
        if (!Array.isArray(source) || source.length === 0) return [];

        if (!columnsEnabled) {
            return source.map((cell) => (
                <DataGridBodyCell
                    key={cell.column.name}
                    density={density}
                    cell={cell}
                    rowKey={row?.key}
                    rowSelected={row?.selected}
                    hasDetailedPanel={hasDetailedPanel}
                    detailedPanelVisible={detailedPanelVisible}
                    detailedPanelOpen={detailedPanelOpen}
                    toggleSelection={toggleSelection}
                    setDetailedPanelOpen={setDetailedPanelOpen}
                    groupedRowsVisible={groupedRowsVisible}
                    groupedRowsOpen={groupedRowsOpen}
                    setGroupedRowsOpen={setGroupedRowsOpen}
                    startScrolling={startScrolling}
                />
            ));
        }

        const leftPinnedCells = [];
        const rightPinnedCells = [];
        const centerCandidates = [];

        for (const cell of source) {
            const col = cell?.column;
            if (!col) continue;
            if (col?.pinnedType === DataGridColumnPinnedTypes.left) leftPinnedCells.push(cell);
            else if (col?.pinnedType === DataGridColumnPinnedTypes.right) rightPinnedCells.push(cell);
            else centerCandidates.push(cell);
        }

        const centerVisible = visibleCenterColumnKeys == null
            ? centerCandidates
            : centerCandidates.filter(c =>
                new Set(visibleCenterColumnKeys.map(String)).has(String(c?.column?.name)),
            );

        const renderCell = (cell) => {
            const col = cell.column;
            const w = getColWidth(col);
            const baseCellStyle = {minHeight: contentMinHeight, width: w};
            let stickyStyle = {};

            if (col?.pinnedType === DataGridColumnPinnedTypes.left) {
                stickyStyle = {
                    position: 'sticky',
                    left: col.leftPinOffset ?? 0,
                    zIndex: 2,
                };
            } else if (col?.pinnedType === DataGridColumnPinnedTypes.right) {
                stickyStyle = {
                    position: 'sticky',
                    right: col.rightPinOffset ?? 0,
                    zIndex: 2,
                };
            }

            return (
                <DataGridBodyCell
                    key={col.name}
                    density={density}
                    cell={cell}
                    rowKey={row?.key}
                    rowSelected={row?.selected}
                    hasDetailedPanel={hasDetailedPanel}
                    detailedPanelVisible={detailedPanelVisible}
                    detailedPanelOpen={detailedPanelOpen}
                    toggleSelection={toggleSelection}
                    setDetailedPanelOpen={setDetailedPanelOpen}
                    groupedRowsVisible={groupedRowsVisible}
                    groupedRowsOpen={groupedRowsOpen}
                    setGroupedRowsOpen={setGroupedRowsOpen}
                    startScrolling={startScrolling}
                    tdStyle={{...baseCellStyle, ...stickyStyle}}
                    rowLockHeight={lockedRowHeight}
                />
            );
        };

        const result = [];

        result.push(...leftPinnedCells.map(renderCell));
        if (leadingSpacerWidth > 0) {
            result.push(
                <td key={'spacer-left-inner'} aria-hidden className="data-grid-spacer-cell" style={{width: leadingSpacerWidth}}/>
            )
        }

        result.push(...centerVisible.map(renderCell));

        if (trailingSpacerWidth > 0) {
            result.push(
                <td key={'spacer-right-inner'} aria-hidden className="data-grid-spacer-cell" style={{width: trailingSpacerWidth}}/>
            )
        }

        result.push(...rightPinnedCells.map(renderCell));

        return result;
    }, [
        row?.cells,
        row?.key,
        row?.selected,
        density,
        columnsEnabled,
        hasDetailedPanel,
        detailedPanelVisible,
        detailedPanelOpen,
        groupedRowsVisible,
        groupedRowsOpen,
        getColWidth,
        contentMinHeight,
        lockedRowHeight,
        visibleCenterColumnKeys,
        leadingSpacerWidth,
        trailingSpacerWidth,
        startScrolling,
        toggleSelection,
    ]);

    const trProps = useMemo(() =>
        Object.keys(row)
            .filter(key =>
                key !== 'cells' &&
                key !== 'key' &&
                key !== 'data' &&
                key !== 'selected' &&
                key !== 'detailedPanel' &&
                key !== 'groupedRows' &&
                key !== '_cellsRaw' &&
                key !== 'colCount' &&
                key !== 'stickToBottom' &&
                key !== 'stickToTop' &&
                key !== 'ref' &&
                key !== 'style'
            )
            .reduce((acc, key) => {
                if (typeof row[key] !== 'undefined') {
                    acc[key] = row[key];
                }
                return acc;
            }, {}), [row])

    return (
        <>
            <tr
                {...trProps}
                ref={ref}
                data-grid-row-key={row.key}
                style={columnsEnabled ? {...(row?.style ?? {}), minHeight: trMinHeight, height: trMinHeight} : undefined}
                className={classnames(
                    'data-grid-body-row',
                    classNames.row,
                    className,
                    {
                        [classNames.selectedRow]: row.selected,
                        'selected': row.selected,
                        [row.className]: !!row.className,
                    },
                )}
            >
                {cells}
            </tr>
            {
                hasDetailedPanel &&
                <DataGridBodyRowDetailedPanel
                    row={row}
                    visible={detailedPanelVisible}
                />
            }
            {
                hasGroupedRows &&
                <DataGridBodyGroupedRows
                    row={row}
                    visible={groupedRowsVisible}
                    startScrolling={startScrolling}
                    alternateColor={!className?.includes('data-grid-grouped-row')}
                    {...groupedRowVirtProps}
                />
            }
        </>
    )
}

const DataGridBodyRow = forwardRef(_DataGridBodyRow);

export default DataGridBodyRow;
